import { useMutation, useQueryClient } from "@enkor/react-query";

import BaseApi from "@logics/api/baseApi";
import useModalState from "@logics/hooks/useModalState";
import { eventListQueryKeys } from "@domains/eventList/constants/eventListQueryKeys";
import { ToastSuccess, ToastError } from "@logics/utils/toast";
import { myPageQueryKeys } from "@domains/myPage/constants/myPageQueryKeys";
import {
  RequestCreateEventComment,
  RequestCreateEventCommentReply,
  RequestCreateEventNoShow,
  RequestEventCancel,
  RequestUpdateEventComment,
} from "@models/data-contracts";

import { eventQueryKeys } from "../constants/eventQueryKeys";
import { eventMutationKeys } from "../constants/eventMutationKeys";

const eventApi = new BaseApi("/event");
const eventApiV2 = new BaseApi("/event", "v2");

// ------------------------------------------------------------------------

export const useDeleteEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => eventApi.delete(`/${eventId}`),
    onSuccess: () => {
      ToastSuccess("Gathering deleted!");

      queryClient.invalidateQueries({
        queryKey: eventListQueryKeys.getEventList(),
      });
      queryClient.invalidateQueries({
        queryKey: myPageQueryKeys.eventListKey,
      });
    },
    onError: () => {
      ToastError("Unable to delete the gathering.");
    },
  });
};

// ------------------------------------------------------------------------

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: eventMutationKeys.mutateEventComments(["CREATE"]),
    mutationFn: ({
      eventId,
      text,
    }: RequestCreateEventComment & { eventId: string }) =>
      eventApi.post(`/${eventId}/comment`, { text }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventComments({ eventId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useUpdateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: eventMutationKeys.mutateEventComments(["UPDATE"]),
    mutationFn: ({
      eventId,
      commentId,
      text,
    }: RequestUpdateEventComment & {
      eventId: string;
      commentId: number;
    }) => eventApi.patch(`/${eventId}/comment/${commentId}`, { text }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventComments({ eventId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationFn: ({
      eventId,
      commentId,
    }: {
      eventId: string;
      commentId: number;
    }) => eventApi.delete(`/${eventId}/comment/${commentId}`),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventComments({ eventId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useCreateReplyMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: eventMutationKeys.mutateCommentReplies(["CREATE"]),
    mutationFn: ({
      eventId,
      commentId,
      text,
    }: RequestCreateEventCommentReply & {
      eventId: string;
      commentId: number;
    }) => eventApi.post(`/${eventId}/comment/${commentId}/reply`, { text }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventComments({ eventId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useUpdateReplyMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationKey: eventMutationKeys.mutateCommentReplies(["UPDATE"]),
    mutationFn: ({
      eventId,
      commentId,
      replyId,
      text,
    }: RequestCreateEventCommentReply & {
      eventId: string;
      commentId: number;
      replyId: number;
    }) =>
      eventApi.patch(`/${eventId}/comment/${commentId}/reply/${replyId}`, {
        text,
      }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventComments({ eventId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useDeleteReplyMutation = () => {
  const queryClient = useQueryClient();
  const { closeModal } = useModalState();

  return useMutation({
    mutationFn: ({
      eventId,
      commentId,
      replyId,
    }: {
      eventId: string;
      commentId: number;
      replyId: number;
    }) => eventApi.delete(`/${eventId}/comment/${commentId}/reply/${replyId}`),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventComments({ eventId }),
      });

      closeModal();
    },
  });
};

// ------------------------------------------------------------------------

export const useCreateEventRegisterMutation = () => {
  return useMutation({
    mutationFn: (eventId: string) => eventApi.post(`${eventId}/join`),
  });
};

// ------------------------------------------------------------------------

export const useCreateEventUnregisterMutation = () => {
  return useMutation({
    mutationFn: ({
      eventId,
      reason,
    }: RequestEventCancel & {
      eventId: string;
    }) =>
      eventApiV2.post(`${eventId}/leave`, {
        reason,
      }),
  });
};

// ------------------------------------------------------------------------

export const useCreateCheckAttendanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      eventUserId,
    }: {
      eventId: string;
      eventUserId: string;
    }) => eventApi.post(`${eventId}/user/${eventUserId}`),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventUserList({ eventId }),
      });
    },
  });
};

// ------------------------------------------------------------------------

export const useCreateNoShowReasonMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      content,
    }: { eventId: string } & RequestCreateEventNoShow) =>
      eventApi.post(`/no-show/${eventId}`, {
        content,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getRecentNoShowEvent(),
      });
    },
  });
};
